import { useCallback } from "react"
import Link from "next/link"

import { SOFIA_DISPLAY_NAME } from "@/constants/sofia"
import { Button, Skeleton } from "@coval/ui"

import { formatRelativeTimestampWithTime } from "@/utils/formatting/format-timestamp"

import { useInvestigationFindingActionHandler } from "../hooks/useInvestigationFindingAction"
import {
  loadInvestigationEvidence,
  useInvestigationFindings,
} from "../hooks/useInvestigationFindings"
import type {
  InvestigationFinding,
  InvestigationFindingsSnapshot,
} from "../utils/investigationFindings"
import { HomeFindingRow } from "./HomeFindingRow"

interface HomeFindingsSectionProps {
  basePath: string
  enabled: boolean
  workspaceId: string | null
  organizationSlug?: string | null
}

function freshnessLine(snapshot: InvestigationFindingsSnapshot): string {
  const parts: string[] = []
  // Format first: an unparseable checkpoint formats to "", which would otherwise leave
  // a prefix with nothing after it.
  const simulation = formatRelativeTimestampWithTime(
    snapshot.freshness.simulation?.analyzedThrough,
  )
  const conversation = formatRelativeTimestampWithTime(
    snapshot.freshness.conversation?.analyzedThrough,
  )
  if (simulation) {
    parts.push(`Simulations ${simulation}`)
  }
  if (conversation) {
    parts.push(`Conversations ${conversation}`)
  }
  // Only claim what the server proved: with no readable checkpoint there is no honest
  // way to say "everything is analyzed" or "0 pending", so say freshness is unknown.
  return parts.length > 0
    ? `Analyzed through: ${parts.join(" · ")}`
    : "Analysis freshness unknown"
}

export function HomeFindingsSection({
  basePath,
  enabled,
  workspaceId,
  organizationSlug,
}: HomeFindingsSectionProps) {
  const { patchFinding, refetch, snapshot, status } = useInvestigationFindings({
    enabled,
    view: "briefing",
    workspaceId,
    organizationSlug,
  })

  const handleAction = useInvestigationFindingActionHandler({
    organizationSlug,
    patchFinding,
    refetch,
    workspaceId,
  })

  const handleLoadEvidence = useCallback(
    async (finding: InvestigationFinding) => {
      if (!workspaceId) {
        return null
      }
      return loadInvestigationEvidence({
        basePath,
        findingId: finding.findingId,
        organizationSlug,
        workspaceId,
      })
    },
    [basePath, workspaceId, organizationSlug],
  )

  // Nothing to render: the surface is disabled, the workspace is unresolved, or Sofia
  // answered that this organization has no findings surface at all.
  if (!enabled || !workspaceId || status === "unavailable") {
    return null
  }

  if (status === "loading") {
    return (
      <section
        aria-label="Loading recent investigations"
        role="status"
        className="space-y-2"
        data-testid="home-findings-loading"
        data-covi-view-list="investigation"
        data-covi-list-filters={`workspace=${workspaceId}; view=briefing`}
        data-covi-list-showing="loading; briefing query incomplete"
      >
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-16 w-full" />
      </section>
    )
  }

  if (status === "error" || snapshot === null) {
    return (
      <section
        aria-labelledby="home-findings-title"
        className="space-y-2"
        data-covi-view-list="investigation"
        data-covi-list-filters={`workspace=${workspaceId}; view=briefing`}
        data-covi-list-showing="error; briefing query incomplete"
      >
        <h2
          id="home-findings-title"
          className="text-base font-semibold text-coval-text"
        >
          Recent Investigations
        </h2>
        <div className="flex items-center justify-between gap-4 rounded-md border border-coval-border bg-surface-default p-4">
          <p className="text-sm text-coval-text-muted">
            {SOFIA_DISPLAY_NAME} couldn&apos;t load recent investigations.
          </p>
          <Button type="button" variant="secondary" onClick={refetch}>
            Retry
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section
      aria-labelledby="home-findings-title"
      // The briefing header sits on the page background; only the findings
      // themselves are carded, one card per finding.
      className="space-y-4"
      data-covi-view-list="investigation"
      data-covi-list-filters={`workspace=${workspaceId}; view=briefing`}
      data-covi-list-sort="priority asc"
      data-covi-list-total={
        snapshot.truncated ? undefined : snapshot.findings.length
      }
      data-covi-list-showing={`${snapshot.findings.length} priority findings loaded${
        snapshot.truncated ? "; briefing scan incomplete" : ""
      }`}
    >
      <div className="space-y-1">
        <h2
          id="home-findings-title"
          className="text-base font-semibold text-coval-text"
        >
          Recent Investigations
        </h2>
        <p className="text-sm text-coval-text-muted">
          {snapshot.briefing?.selectionMode === "recent" &&
          snapshot.findings.length > 0
            ? `${snapshot.findings.length} most recent · nothing new or recurring since ${formatRelativeTimestampWithTime(snapshot.briefing.windowStart)}`
            : snapshot.briefing?.windowStart
              ? `Top ${snapshot.findings.length} of ${snapshot.briefing.eligibleFindingCount} since ${formatRelativeTimestampWithTime(snapshot.briefing.windowStart)}`
              : `Top findings from ${SOFIA_DISPLAY_NAME}'s latest scan`}{" "}
          <Link
            href={`${basePath}/home/investigations`}
            className="font-medium text-coval-info-text hover:underline"
            aria-label="View all investigations"
          >
            View all
          </Link>
        </p>
        <p className="text-xs text-coval-text-muted">
          {freshnessLine(snapshot)}
        </p>
      </div>
      {snapshot.findings.length > 0 ? (
        <>
          <ul className="space-y-3">
            {snapshot.findings.map((finding) => (
              <HomeFindingRow
                key={finding.findingId}
                finding={finding}
                loadEvidence={handleLoadEvidence}
                onAction={handleAction}
              />
            ))}
          </ul>
          {snapshot.truncated && (
            <p className="text-sm text-coval-text-muted">
              {SOFIA_DISPLAY_NAME} hit the scan limit; more findings may exist
              in the full history.
            </p>
          )}
          {/* The section header's link is the reader's way out mid-scan; this
              is the one for someone who has read to the bottom of the cards and
              is looking for what comes next. Secondary, because the findings
              themselves are what the page is asking to be acted on. */}
          <div className="flex justify-center">
            <Button asChild variant="secondary">
              <Link href={`${basePath}/home/investigations`}>
                All Investigations
              </Link>
            </Button>
          </div>
        </>
      ) : (
        <p className="rounded-md border border-coval-border bg-surface-default p-5 text-sm text-coval-text-muted">
          No new or recurring findings since the last briefing. Older findings
          remain in the full history.
        </p>
      )}
    </section>
  )
}
